import { Locale, Locales, useAuth, useLogger } from "@amzn/xb-hooks";
import { useQuery } from "@apollo/client";
import { useConfig } from "../../hooks/useConfig";
import { GET_CURRENT_USER } from "../../utils";
import { useGetApolloClient } from "../ApolloClientProvider/useGetApolloClient";

/*
  This hook determines the post-auth locale for the user.
  The order of precedence for determining the locale is:
  1. User's language preference from User service (if authenticated) and locale is suppported.
  2. null (if loading, not authenticated, currentUser error, or preference language is undefined or not supported).
*/
export const usePostAuthLocale = (): Locale | null => {
  const { log } = useLogger();
  const { isAuthed, isLoading } = useAuth();
  const { loading, error, data } = useQuery(GET_CURRENT_USER, {
    skip: !isAuthed || isLoading,
    // This is the only place we need to make a graphql operation outside of the ApolloProvider context. Because of this,
    // we must provide the ApolloClient directly. Any calls made further down in the tree, within the ApolloProvider are
    // able to use the client provided through that Context.
    client: useGetApolloClient("EstablishLocalePreferenceClient"),
  });
  const { supportedLocales } = useConfig();
  const localeLookup = Locales.lookupFor(supportedLocales);
  log.debug("usePostAuthLocale - useAuth and useQuery value...", { isAuthed, isLoading, loading });
  if (isLoading || !isAuthed || loading) {
    log.debug("usePostAuthLocale - loading...");
    return null;
  } else if (error) {
    log.error("usePostAuthLocale - Error fetching current user...", error);
    return null;
  }
  return localeLookup(data?.currentUser?.preferences?.language) || null;
};
